<template>
    <p-feature-flag
        name="Accounting"
        redirect>
        <p-form ref="addGeneralLedgerForm" @submit="onSubmit">
            <div class="container-fluid content-wrapper" v-if="glEntry">
                <b-row class="content-heading">
                    <b-col>
                        <div>Add General Ledger Entry</div>
                    </b-col>
                    <b-col class="text-end">
                        <p-button type="submit" :is-busy="isBusy" :disabled="isEmpty" variant="primary">Save</p-button>
                    </b-col>
                </b-row>
                <p-card>
                    <b-row>
                        <b-col cols="auto" class="me-auto mb-2">
                            <GLEntryLine
                                :is-busy="isBusy"
                                @ok="handleAddGlEntryLine"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12">
                            <p-table
                                ref="glEntryTable"
                                :items="glEntry.details"
                                :fields="fields"
                                :per-page="100"
                                :show-select-all="false"
                                :enable-row-selection="false"
                                class="wide mt-2"
                                outlined
                                empty-text="Use 'Add Line' to add detail lines to the entry."
                                :pagination="false"
                                :show-record-count="false"
                            >
                                <template
                                    v-slot:cell(chartOfAccountsId)="{value, item}"
                                >
                                    <p-select
                                        data-type="chartOfAccounts"
                                        v-model="item.chartOfAccountsId"
                                        name="GL Code"
                                        rules="required"
                                    />
                                </template>
                                <template
                                    v-slot:cell(entryReference)="{value, item}"
                                >
                                    <p-input
                                        v-model="item.entryReference"
                                        name="Entry Reference"
                                        rules="required|max:4000" />
                                </template>
                                <template
                                    v-slot:cell(credit)="{value, item}"
                                >
                                    <p-number
                                        input-type="money"
                                        v-model="item.credit"
                                        name="Credit"
                                        :disabled="debitIsSet(item)"
                                        :rules="debitIsSet(item) ? '' : 'min_value:0'"
                                        :add-money-rules="!debitIsSet(item)"
                                        left-align />
                                </template>
                                <template
                                    v-slot:cell(debit)="{value, item}"
                                >
                                    <p-number
                                        input-type="money"
                                        name="Debit"
                                        v-model="item.debit"
                                        :disabled="creditIsSet(item)"
                                        :rules="creditIsSet(item) ? '' : 'min_value:0'"
                                        :add-money-rules="!creditIsSet(item)"
                                        left-align />
                                </template>
                                <template v-slot:cell(removeDetail)="{ value, item }">
                                    <p-button
                                        class="p-1 ms-1 short-button" 
                                        @click="confirmDeleteDetail(item)">
                                        <BIconTrash icon="trash" ></BIconTrash>
                                    </p-button>
                                </template>
                            </p-table>
                        </b-col>
                    </b-row>  
                    <b-row>
                        <b-col>
                            <p-number
                                label="Total Credit"
                                input-type="money"
                                :model-value="totalCredit"
                                :disabled="true"
                            />
                        </b-col>
                        <b-col>
                            <p-number
                                label="Total Debit"
                                input-type="money"
                                :model-value="totalDebit"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>                 
                </p-card>
                <b-row class="mt-4">
                    <b-col class="text-center mb-3">
                        <p-button
                            variant="primary"
                            type="submit"
                            :is-busy="isBusy"
                            :disabled="isEmpty"
                            class="text-end">
                            Save
                        </p-button>
                    </b-col>
                </b-row>
            </div>
        </p-form>
    </p-feature-flag>
</template>

<script>
import axios from "axios";
import NavigationGuard from '@/components/mixins/NavigationGuard.js'
import { useSelectListsStore } from '@/stores/selectLists.js';
import editPage from '@/components/mixins/EditPage';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import { currencyFormatter } from "../../components/Common/Formatters";
import GLEntryLine from "../../components/GLEntryLine.vue";

const generalLedgerEntryType = {
    manualEntry: 25
}

export default {
    mixins: [NavigationGuard, editPage],
    components: {
        GLEntryLine
    },
    data() {
        return {
            glEntry: {},
            isBusy: false,
            fields: [
                {
                    key: 'chartOfAccountsId',
                    label: 'GL Code'
                },
                {
                    key: 'entryReference',
                    label: 'Entry Reference'
                },
                {
                    key: 'credit',
                    label: 'Credit',
                    formatter:currencyFormatter,
                    footerFormatter:currencyFormatter
                },
                {
                    key: 'debit',
                    label: 'Debit',
                    formatter:currencyFormatter,
                    footerFormatter:currencyFormatter
                },
                {
                    key: 'removeDetail',
                    label: ''
                }
            ]
        };
    },
    watch: {
        glEntry: NavigationGuard.$watcher,
        $route: function(to, from) {
            if (to !== from) {
                this.loadData();
            }
        }
    },
    computed: {
        chartOfAccounts() {
            return useSelectListsStore().lists.chartOfAccounts ?? [];
        },
        totalCredit() {
                return this.glEntry.details?.reduce((accumulator, currentValue) => accumulator + (Number(currentValue.credit) || 0), 0) ?? 0;
        },
        totalDebit() {
                return this.glEntry.details?.reduce((accumulator, currentValue) => accumulator + (Number(currentValue.debit) || 0), 0) ?? 0;
        },
        isEmpty() {
            return this.totalCredit + this.totalDebit == 0;
        }
    },
    methods: {                
        async onSubmit() {
            if ((!this.totalCredit && !this.totalDebit) || this.totalCredit + this.totalDebit == 0) {
                this.$appError('Please add at least one credit or one debit value.');
                return;
            }
            this.isBusy = true;
            this.glEntry.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            this.glEntry.details.forEach(detail => {
                let selectedChartOfAccounts = this.chartOfAccounts.find(coa => coa.value == detail.chartOfAccountsId);
                detail.generalLedgerEntryTypeId = generalLedgerEntryType.manualEntry;
                detail.glCode = selectedChartOfAccounts.glCode;
                detail.glCodeDescription = selectedChartOfAccounts.glCodeDescription;
            });

            axios.post("generalLedgers", this.glEntry)
                .then(() => {
                    this.dataSaved();
                    this.$appSuccess(`General Ledger entry saved successfully.`);
                })
                .finally(() => {
                    this.isBusy = false;
                    this.$router.push("/generalledgers");
                });
        },
        loadData() {
            let promise =  axios.get('generalledgers/new');
            promise.then(resp => {
                this.glEntry = resp.data;
                this.dataLoaded();
            })
        },
        creditIsSet(line) {
            return line.credit;
        },
        debitIsSet(line) {
            return line.debit;
        },
        async handleAddGlEntryLine(line) {
            this.glEntry.details.push({
                chartOfAccountsId: line.chartOfAccountsId,
                entryReference: line.entryReference,
                generalLedgerEntryTypeId: generalLedgerEntryType.manualEntry,
                credit: line.credit,
                debit: line.debit
            });
        },
        confirmDeleteDetail(detail) {
            this.$bvModal
                .msgBoxConfirm(
                    'Are you sure you want to remove this line from the GL entry?'
                )
                .then(value => {
                    if (value) {
                        this.deleteDetail(detail);
                    }
                });
        },
        async deleteDetail(detail) {
            let index = this.glEntry.details.indexOf(detail);
            if (index !== -1) {
                this.glEntry.details.splice(index, 1);
            }
        }
    },
    mounted: function() {
        this.loadData();
    }
};
</script>
